import { combineEpics } from 'redux-observable';
import { catchError } from 'rxjs/operators';
import * as Sentry from '@sentry/react';
import { epics as authEpics } from 'modules/auth';
import { epics as appointmentsEpics } from 'modules/appointments';
import { epics as scheduleEpics } from 'modules/schedule';
import { epics as casesEpics } from 'modules/cases';
import { epics as appointmentServiceTypesEpics } from 'modules/appointmentServiceTypes';
import { epics as therapistEpics } from 'modules/therapist';
import { epics as leadEpics } from 'modules/leads';
import { epics as patientEpics } from 'modules/patients';
import { epics as insuranceEpics } from 'modules/insurance';
import { epics as paymentEpics } from 'modules/payment';
import { epics as patientReferralEpics } from 'modules/patientReferral';
import { epics as offerEpics } from 'modules/offers';
import { epics as hookEpics } from 'modules/hooks';
import { epics as fileEpics } from 'modules/files';
import { epics as calendarEpics } from 'modules/calendar';
import { epics as offlineSyncEpics } from 'modules/offlineSync';
import { RootEpic } from 'types/types';

const combinedEpics = combineEpics(
  appointmentsEpics,
  appointmentServiceTypesEpics,
  authEpics,
  casesEpics,
  leadEpics,
  scheduleEpics,
  therapistEpics,
  patientEpics,
  insuranceEpics,
  paymentEpics,
  patientReferralEpics,
  offerEpics,
  hookEpics,
  fileEpics,
  calendarEpics,
  offlineSyncEpics,
);

/**
 * Keeps one epic's error from taking the rest of the app down with it.
 *
 * `combineEpics` merges every epic into a single stream, and an error on that
 *   stream terminates it. There is no partial failure: the epic middleware
 *   unsubscribes and *every* epic stops, so no request, booking, payment or
 *   sync runs again until the page is reloaded. The UI keeps rendering, which
 *   is what makes it so hard to notice — buttons do nothing and nothing
 *   explains why.
 *
 * That is reachable today, not hypothetically. `uploadAppFile$` maps with an
 *   `async` function that awaits `fileToBase64`, which rejects when
 *   `FileReader` fails; nothing catches it, so a file the browser cannot read
 *   ends the root epic. A local `catchError` handles that one case, and this is
 *   the net for the ones nobody has thought of — the file above carried a
 *   `// Consider adding global error handler` note for exactly this.
 *
 * **Returning `source` resubscribes.** Without it the catch would be worse than
 *   the disease: it would swallow the error *and* leave the stream completed.
 *   Resubscribing re-establishes every epic's listener on `action$`, so the app
 *   works again from the next action on. What is lost is whatever was in flight
 *   when the error hit — a pending request's response is dropped, since its
 *   inner observable was torn down. That is the trade, and it is the right way
 *   round: one lost response against every epic being dead for the session.
 *
 * Epics holding state in a closure survive it. `resolveBookingConflict$`'s
 *   `awaitingRefetch` is created when the epic function is called, which
 *   happens once, and its `finalize` runs during the teardown — so the count
 *   returns to zero rather than drifting.
 *
 * Reported as an exception, not a message: this is a real defect every time it
 *   fires, and it needs the stack to find which epic threw.
 */
const rootEpic: RootEpic = (action$, state$, dependencies) =>
  combinedEpics(action$, state$, dependencies).pipe(
    catchError((error, source) => {
      Sentry.captureException(error, {
        tags: { area: 'root-epic' },
      });
      return source;
    }),
  );

export default rootEpic;
